import React from 'react';
import Select from 'shared/components/UC/Select';
import { AlertStatus, AlertFilters, AlertType, AlertPriority } from '../../types/admin';
import { ALERT_CATALOG } from './alertCatalog';
import GroupedMultiSelect from './GroupedMultiSelect';
import styles from './StatusTableFilters.module.css';
import Button from 'shared/components/UC/Button';

interface AlertsFiltersProps {
  filters: AlertFilters;
  onFilterChange: (key: keyof AlertFilters, value: string) => void;
  onTypesChange: (types: AlertType[]) => void;
  onClearFilters?: () => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  typeCounts?: Partial<Record<AlertType, number>>;
}

const TYPE_ORDER: AlertType[] = [
  AlertType.INVITATION_REJECTED,
  AlertType.INVITATION_NO_RESPONSE,
  AlertType.EVALUATOR_NO_LOGIN,
  AlertType.EVALUATION_DELAYED,
  AlertType.EVALUATOR_OVERLOADED,
  AlertType.EVALUATION_SCORE_VARIANCE,
  AlertType.MANUAL,
];

const AlertsFilters: React.FC<AlertsFiltersProps> = ({
  filters,
  onFilterChange,
  onTypesChange,
  onClearFilters,
  searchTerm,
  onSearchChange,
  typeCounts = {},
}) => {
  const statusOptions = [
    { value: AlertStatus.OPEN, label: 'Abierta' },
    { value: AlertStatus.RESOLVED, label: 'Resuelta' },
    { value: AlertStatus.DISMISSED, label: 'Descartada' },
  ];

  const priorityOptions = [
    { value: AlertPriority.LOW,    label: 'Baja' },
    { value: AlertPriority.MEDIUM, label: 'Media' },
    { value: AlertPriority.HIGH,   label: 'Alta' },
    { value: AlertPriority.URGENT, label: 'Urgente' },
  ];

  const hasActiveFilters =
    filters.status ||
    (filters.alertTypes?.length ?? 0) > 0 ||
    filters.applicationId ||
    filters.evaluatorId ||
    filters.priority ||
    searchTerm;

  return (
    <div className={styles.filters}>
      <div className={styles.filterGroup}>
        <Select
          label="Estado"
          id="filter-status"
          value={filters.status || ''}
          onChange={(e) => onFilterChange('status', e.target.value)}
          options={statusOptions}
          placeholder="Todos los estados"
        />
      </div>

      <div className={styles.filterGroup}>
        <label className={styles.filterLabel}>Tipo de Alerta</label>
        <GroupedMultiSelect
          groups={[{
            options: TYPE_ORDER.map((t) => {
              const entry = ALERT_CATALOG.find((e) => e.type === t);
              const count = typeCounts[t];
              const label = entry?.label ?? t;
              return { value: t, label: count ? `${label} (${count})` : label };
            }),
          }]}
          selected={filters.alertTypes ?? []}
          onChange={(vals) => onTypesChange(vals as AlertType[])}
          placeholder="Todos los tipos"
        />
      </div>

      <div className={styles.filterGroup}>
        <Select
          label="Prioridad"
          id="filter-priority"
          value={filters.priority || ''}
          onChange={(e) => onFilterChange('priority', e.target.value)}
          options={priorityOptions}
          placeholder="Todas las prioridades"
        />
      </div>

      {hasActiveFilters && onClearFilters && (
        <Button
          onClick={onClearFilters}
          variant="secondary"
          size="sm"
          className={styles.clearButton}
        >
          Limpiar Filtros
        </Button>
      )}
    </div>
  );
};

export default AlertsFilters;
